/**
 * useRecentPages Hook
 * 
 * Tracks recently visited pages for quick navigation.
 * Persists to localStorage and provides history dropdown data.
 * 
 * @module hooks/useRecentPages
 */
import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

const MAX_RECENT_PAGES = 10;
const STORAGE_KEY = 'wms_recent_pages';

// Page title mapping (path -> label)
const PAGE_TITLES = {
    '/admin/dashboard': 'Dashboard',
    '/admin/orders': 'Orders',
    '/admin/products': 'Products',
    '/admin/inventory': 'Inventory',
    '/admin/users': 'Users',
    '/admin/warehouses': 'Warehouses',
    '/admin/shipping': 'Shipping',
    '/admin/hr-management': 'HR',
    '/admin/financial-management': 'Finance',
    '/admin/analytics': 'Analytics',
    '/admin/marketplace-connections': 'Marketplaces',
    '/admin/ai-assistant': 'AI Assistant',
    '/admin/settings': 'Settings',
    '/admin/reports': 'Reports',
    '/client/dashboard': 'Client Dashboard',
    '/client/orders': 'My Orders',
    '/manager/dashboard': 'Manager Dashboard',
    '/operator/dashboard': 'Operator Dashboard'
};

const getPageTitle = (path) => {
    // Check exact match first
    if (PAGE_TITLES[path]) return PAGE_TITLES[path];

    // Check prefix matches
    for (const [key, value] of Object.entries(PAGE_TITLES)) {
        if (path.startsWith(key)) return value;
    }

    // Fallback: extract from path
    const segments = path.split('/').filter(Boolean);
    const lastSegment = segments[segments.length - 1] || 'Page';
    return lastSegment.charAt(0).toUpperCase() + lastSegment.slice(1).replace(/-/g, ' ');
};

const useRecentPages = () => {
    const location = useLocation();
    const [recentPages, setRecentPages] = useState([]);

    // Load from localStorage on mount
    useEffect(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                setRecentPages(JSON.parse(stored));
            }
        } catch (e) {
            console.error('Error loading recent pages:', e);
        }
    }, []);

    // Track page visits
    useEffect(() => {
        const currentPath = location.pathname;

        // Skip certain paths
        if (currentPath === '/login' || currentPath === '/') return;

        setRecentPages(prev => {
            const newEntry = {
                path: currentPath,
                title: getPageTitle(currentPath),
                timestamp: Date.now()
            };

            // Remove duplicate if exists
            const filtered = prev.filter(p => p.path !== currentPath);

            // Add to front, limit to max
            const updated = [newEntry, ...filtered].slice(0, MAX_RECENT_PAGES);

            // Persist
            try {
                localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
            } catch (e) {
                console.error('Error saving recent pages:', e);
            }

            return updated;
        });
    }, [location.pathname]);

    // Clear history
    const clearHistory = useCallback(() => {
        setRecentPages([]);
        localStorage.removeItem(STORAGE_KEY);
    }, []);

    // Remove single page
    const removePage = useCallback((path) => {
        setRecentPages(prev => {
            const updated = prev.filter(p => p.path !== path);
            localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
            return updated;
        });
    }, []);

    return {
        recentPages,
        currentPage: location.pathname,
        clearHistory,
        removePage
    };
};

export default useRecentPages;
